/**
 * Configuração de runtime por cliente (RD, SEFAZ-MT, ...).
 *
 * Ordem de precedência:
 *   1. `window.__PM_CONFIG__` — definido por `public/config.js`, carregado pelo index.html antes do bundle.
 *      Em container, o entrypoint do nginx reescreve esse arquivo a partir de variáveis PM_* no start.
 *   2. Variáveis `VITE_*` em tempo de build.
 *   3. Defaults abaixo.
 */

import { applyBrandPalette } from "./brandColor";

export type AudioMode = "url" | "download";

export type PmRuntimeConfig = {
  apiBaseUrl?: string;
  wsBaseUrl?: string;
  authAudience?: string;
  appTitle?: string;
  appSubtitle?: string;
  primaryColor?: string;
  loginBackgroundUrl?: string;
  logoUrl?: string;
  audioMode?: AudioMode | string;
  audioDownloadPath?: string;
  appVersion?: string;
  journeyBaseUrl?: string;
};

declare global {
  interface Window {
    __PM_CONFIG__?: PmRuntimeConfig;
  }
}

function runtimeConfig(): PmRuntimeConfig {
  if (typeof window === "undefined") return {};
  const cfg = window.__PM_CONFIG__;
  return cfg && typeof cfg === "object" ? cfg : {};
}

/** Valor não vazio (placeholders `${...}` não substituídos contam como vazio). */
function pick(...values: (string | undefined | null)[]): string | undefined {
  for (const v of values) {
    if (v == null) continue;
    const s = String(v).trim();
    if (!s || /^\$\{.*\}$/.test(s)) continue;
    return s;
  }
  return undefined;
}

const rc = runtimeConfig();

function stripTrailingSlash(url: string) {
  return url.length > 1 ? url.replace(/\/+$/, "") : url;
}

export const API_BASE_URL = stripTrailingSlash(
  pick(rc.apiBaseUrl, import.meta.env.VITE_API_BASE_URL) ?? "/phonemanager",
);

export const APP_VERSION = pick(rc.appVersion, import.meta.env.VITE_APP_VERSION) ?? "1.0.0-dev";

export const AUTH_AUDIENCE = pick(rc.authAudience, import.meta.env.VITE_AUTH_AUDIENCE) ?? "phonemanagergui";

/** Endpoint SockJS (http/https). Se `wsBaseUrl` vier com ws:// ou wss://, é convertido para http(s)://. */
const WS_BASE = pick(rc.wsBaseUrl, import.meta.env.VITE_WS_BASE_URL);
export const WS_PATH = WS_BASE
  ? stripTrailingSlash(WS_BASE.replace(/^ws(s?):\/\//i, "http$1://"))
  : `${API_BASE_URL}/ws-pm`;

export const APP_TITLE = pick(rc.appTitle, import.meta.env.VITE_APP_TITLE) ?? "Phone Manager";

export const APP_SUBTITLE = pick(rc.appSubtitle) ?? "Supervisão em tempo real";

/** Cor primária da marca (padrão RD #262362, igual à gui de produção). */
export const PRIMARY_COLOR = pick(rc.primaryColor, import.meta.env.VITE_PRIMARY_COLOR) ?? "#262362";

export const LOGIN_BACKGROUND_URL = pick(rc.loginBackgroundUrl);

export const LOGO_URL = pick(rc.logoUrl);

export const AUDIO_MODE: AudioMode =
  (pick(rc.audioMode, import.meta.env.VITE_AUDIO_MODE) ?? "url").toLowerCase() === "download" ? "download" : "url";

/** Serviço standalone da jornada Nimo (demo WhatsApp → e-mail → ligação). Na nuvem: mesma origem em /jornada. */
export const JOURNEY_BASE_URL = stripTrailingSlash(
  pick(rc.journeyBaseUrl, import.meta.env.VITE_JOURNEY_BASE_URL) ?? "/jornada",
);

export const AUDIO_DOWNLOAD_PATH = pick(rc.audioDownloadPath) ?? "/rest/log/recording/download";

/** Resolve um asset público respeitando o base path do Vite (deploy em /phonemanagergui/). */
export function publicAsset(path: string | undefined): string | undefined {
  if (!path) return undefined;
  if (/^(https?:|data:|blob:|\/\/)/i.test(path)) return path;
  const base = import.meta.env.BASE_URL || "/";
  if (path.startsWith(base)) return path;
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

/**
 * Aplica branding (cor primária + título) no documento. Chamado uma vez no bootstrap.
 * Além de `--color-brand`, calcula variantes com contraste AA (texto sobre a marca, acento
 * legível no claro/escuro) — ver shared/config/brandColor.ts e os tokens em index.css.
 */
export function applyBranding() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (PRIMARY_COLOR && !applyBrandPalette(PRIMARY_COLOR, root)) {
    // Cor que o navegador não entende: mantém o comportamento antigo (CSS cai nos fallbacks).
    root.style.setProperty("--color-brand", PRIMARY_COLOR);
  }
  document.title = APP_TITLE;
}

// Aplica o branding assim que a configuração é lida (antes do primeiro render).
applyBranding();
